// DEV-55. Анти-CSRF-признак для cookie-сессии (БЕЗ-01/БЕЗ-05).
//
// Токен доступа живёт только в httpOnly-cookie и сценарию страницы недоступен.
// Cookie браузер прикладывает сам, поэтому изменяющие запросы обязаны нести
// отдельный признак, который подделать межсайтовым запросом нельзя: сервер
// (double submit) сверяет значение из читаемой cookie `csrf_token` с заголовком
// `X-CSRF-Token`.
//
// Источников признака два, и это не дублирование: тело ответа (`{ csrfToken }`)
// нужно там, где фронт и API на разных доменах (тогда cookie API страница не
// прочитает); читаемая cookie — рабочий источник на одном домене.
//
// DEV-85 (ТЕСТ-Н32). Признак берётся из cookie первым, а память — запасной путь:
// сервер (double submit) сверяет заголовок именно с cookie, а её перевыпускает
// любая вкладка при продлении сессии. Пока память была главнее, вторая вкладка
// после продления в первой слала старый признак и получала 403 — в запросе были и
// cookie, и заголовок, но они не совпадали.

export const CSRF_COOKIE = "csrf_token";
export const CSRF_HEADER = "x-csrf-token";

const MUTATING_METHODS = new Set(["post", "put", "patch", "delete"]);

let inMemoryToken: string | null = null;

/**
 * Признак из тела ответа (`{ csrfToken }`) — запасной источник на случай, когда
 * cookie API странице не видна (фронт и API на разных доменах).
 */
export function rememberCsrfToken(token?: string | null): void {
  if (typeof token === "string" && token.trim() !== "") {
    inMemoryToken = token;
  }
}

export function forgetCsrfToken(): void {
  inMemoryToken = null;
}

export function csrfTokenFromCookie(
  cookie: string = typeof document === "undefined" ? "" : document.cookie,
): string | null {
  if (!cookie) return null;

  const match = cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]*)`));

  return match ? decodeURIComponent(match[1]) : null;
}

export function getCsrfToken(): string | null {
  // Cookie первее памяти: её перевыпускает сервер при каждом продлении сессии —
  // в том числе из другой вкладки, — а заголовок он сверяет именно с ней.
  return csrfTokenFromCookie() || inMemoryToken;
}

/**
 * `DEV-85`: отказ `403` с причиной CSRF — единственный случай, когда изменяющий
 * запрос имеет смысл повторить один раз с заново прочитанным признаком.
 *
 * Сервер отвечает текстом «Изменяющий запрос без действительного
 * анти-CSRF-признака» (`back_balance/middlewares/csrf-middleware.js`), тело —
 * `{ message, errors }`. Разбор терпим и к строке, и к `{ message }`, и к
 * `{ error }`: сцепка не должна держаться на одном написании поля.
 */
export function isCsrfError(error: any): boolean {
  if (error?.response?.status !== 403) {
    return false;
  }

  const body = error.response.data;
  const reason =
    typeof body === "string" ? body : body?.message || body?.error || "";

  return /csrf/i.test(String(reason));
}

export function isMutatingMethod(method?: string): boolean {
  return MUTATING_METHODS.has(String(method || "get").toLowerCase());
}

/**
 * Проставляет признак в изменяющий запрос. Безопасные методы не трогаем: их
 * сервер от проверки освобождает (БЕЗ-05).
 */
export function applyCsrfHeader(config: any): any {
  if (!config || !isMutatingMethod(config.method)) {
    return config;
  }

  const token = getCsrfToken();

  if (!token) {
    return config;
  }

  if (config.headers && typeof config.headers.set === "function") {
    config.headers.set(CSRF_HEADER, token);
  } else {
    config.headers = { ...(config.headers || {}), [CSRF_HEADER]: token };
  }

  return config;
}
